import { checkVerificationCode, sendVerificationCode } from '@/api/fns'
import { ActionContext } from 'vuex'

export interface EmailVerificationState {
  code: string

  isValidCode: boolean

  expCodeDate: Date | null
  attemptId: string | null
  lastCheckTime: number
  timeoutId: number | null

  sendVerificationCodeLoading: boolean
  verifyCodeLoading: boolean

  email: string | null
}

export default {
  namespaced: true,

  state: <EmailVerificationState>{
    code: '',
    isValidCode: false,

    expCodeDate: null,
    attemptId: null,
    lastCheckTime: 0,
    timeoutId: null,

    sendVerificationCodeLoading: false,
    verifyCodeLoading: false,

    email: null,
  },

  getters: {
    isCodeExpired(state: EmailVerificationState): boolean {
      if (!state.expCodeDate) {
        return false
      }

      state.lastCheckTime // eslint-disable-line

      return new Date().getTime() > state.expCodeDate.getTime()
    },
  },

  mutations: {
    setCode(state: EmailVerificationState, code: string) {
      state.code = code
      state.isValidCode = code.length === 6
    },
    setExpCodeDate(state: EmailVerificationState, date: Date | null) {
      state.expCodeDate = date
    },
    setAttemptId(state: EmailVerificationState, attemptId: string | null) {
      state.attemptId = attemptId
    },
    updateCheckTime(state: EmailVerificationState) {
      state.lastCheckTime = Date.now()
    },
    setTimeoutId(state: EmailVerificationState, timeoutId: number | null) {
      state.timeoutId = timeoutId
    },
    setSendVerificationCodeLoading(state: EmailVerificationState, isLoading: boolean) {
      state.sendVerificationCodeLoading = isLoading
    },
    setVerifyCodeLoading(state: EmailVerificationState, isLoading: boolean) {
      state.verifyCodeLoading = isLoading
    },
    setEmail(state: EmailVerificationState, email: string | null) {
      state.email = email
    },
  },

  actions: {
    async sendVerificationCode(
      { commit, dispatch }: ActionContext<EmailVerificationState, any>,
      { email, invoiceInternalId }: { email: string; invoiceInternalId: string },
    ) {
      try {
        commit('setEmail', email)
        commit('setSendVerificationCodeLoading', true)

        const result = await sendVerificationCode(invoiceInternalId, email)
        commit('setAttemptId', result.attempt_id)

        const currentDate = new Date()
        const expDate = new Date(currentDate.getTime() + result.ttl_seconds * 1000)

        commit('setExpCodeDate', expDate)

        dispatch('scheduleExpirationCheck', result.ttl_seconds * 1000)
      } catch (error) {
        console.log('🚀 ~ sendVerificationCode ~ error:', error)
      } finally {
        commit('setSendVerificationCodeLoading', false)
      }
    },

    async retryVerificationCode({
      state,
      commit,
      rootGetters,
      dispatch,
    }: ActionContext<EmailVerificationState, any>) {
      if (!state.email) {
        console.log('No email address available for verification')
        return
      }

      const invoiceId = rootGetters['pay/invoiceId']

      try {
        commit('setSendVerificationCodeLoading', true)

        const result = await sendVerificationCode(invoiceId, state.email)
        commit('setAttemptId', result.attempt_id)

        const currentDate = new Date()
        const expDate = new Date(currentDate.getTime() + result.ttl_seconds * 1000)

        commit('setExpCodeDate', expDate)

        dispatch('scheduleExpirationCheck', result.ttl_seconds * 1000)
      } catch (error) {
        console.log('🚀 ~ retryVerificationCode ~ error:', error)
      } finally {
        commit('setSendVerificationCodeLoading', false)
      }
    },

    async verifyCode({
      state,
      commit,
      rootGetters,
    }: ActionContext<EmailVerificationState, any>) {
      console.log(rootGetters['pay/invoiceId'])

      if (!state.attemptId) {
        console.log('No verification attempt in progress')
        return false
      }

      if (!state.code) {
        console.log('No verification code available')
        return false
      }

      try {
        commit('setVerifyCodeLoading', true)

        const invoiceId = rootGetters['pay/invoiceId']

        await checkVerificationCode(invoiceId, state.attemptId, state.code)

        return true
      } catch (error) {
        commit('setVerifyCodeLoading', false)
        console.log('🚀 ~ sendVerificationCode ~ error:', error)
        return false
      }
    },

    scheduleExpirationCheck(
      { commit, state }: ActionContext<EmailVerificationState, any>,
      delay: number,
    ) {
      if (state.timeoutId) {
        clearTimeout(state.timeoutId)
      }

      const timeoutId = setTimeout(() => {
        commit('updateCheckTime')
        commit('setTimeoutId', null)
      }, delay)

      commit('setTimeoutId', timeoutId)
    },

    checkCodeExpiration({ commit }: ActionContext<EmailVerificationState, any>) {
      commit('updateCheckTime')
    },

    cancelExpirationCheck({ commit, state }: ActionContext<EmailVerificationState, any>) {
      if (state.timeoutId) {
        clearTimeout(state.timeoutId)
        commit('setTimeoutId', null)
      }
    },
  },
}
